import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const VSMContracts = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const navigate = useNavigate();

  const T = {
    title:        { ar: '📋 العقود', en: '📋 Contracts' },
    subtitle:     { ar: 'عقد', en: 'contract(s)' },
    add:          { ar: '+ إنشاء عقد جديد', en: '+ New Contract' },
    search:       { ar: 'ابحث برقم العقد أو العنوان...', en: 'Search by number or title...' },
    contractNo:   { ar: 'رقم العقد', en: 'Contract #' },
    contractTitle:{ ar: 'العنوان', en: 'Title' },
    period:       { ar: 'الفترة', en: 'Period' },
    status:       { ar: 'الحالة', en: 'Status' },
    clients:      { ar: 'العملاء', en: 'Clients' },
    services:     { ar: 'الخدمات', en: 'Services' },
    items:        { ar: 'البنود', en: 'Items' },
    actions:      { ar: 'الإجراءات', en: 'Actions' },
    loading:      { ar: 'جاري التحميل...', en: 'Loading...' },
    noContracts:  { ar: 'لا توجد عقود. اضغط "إنشاء عقد" للبدء.', en: 'No contracts. Click "New Contract" to start.' },
    statusAll:    { ar: 'كل الحالات', en: 'All statuses' },
    statusActive: { ar: 'نشط', en: 'Active' },
    statusPaused: { ar: 'متوقف', en: 'Paused' },
    statusExpired:{ ar: 'منتهي', en: 'Expired' },
    statusCancel: { ar: 'ملغي', en: 'Cancelled' },
    confirmCancel:{ ar: 'هل أنت متأكد من إلغاء هذا العقد؟', en: 'Cancel this contract?' },
    cancelError:  { ar: 'فشل إلغاء العقد', en: 'Failed to cancel' },
    prev:         { ar: '« السابق', en: '« Previous' },
    next:         { ar: 'التالي »', en: 'Next »' },
    page:         { ar: 'صفحة', en: 'Page' },
    of:           { ar: 'من', en: 'of' },
    open:         { ar: 'فتح', en: 'Open' },
    untitled:     { ar: '(بدون عنوان)', en: '(untitled)' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  const STATUS_COLORS = {
    active:    { bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7', label: tr('statusActive') },
    paused:    { bg: 'rgba(245,158,11,0.15)', color: '#fcd34d', label: tr('statusPaused') },
    expired:   { bg: 'rgba(100,116,139,0.2)', color: '#cbd5e1', label: tr('statusExpired') },
    cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#fca5a5', label: tr('statusCancel') },
  };

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ page: pagination.page, limit: 15 });
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);

    try {
      const res = await fetch(`${API}/contracts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setContracts(data.data);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      console.error('[VSM] Fetch contracts error:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pagination.page, statusFilter]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const handleCancel = async (contract, e) => {
    e?.stopPropagation();
    if (!window.confirm(`${tr('confirmCancel')}\n\n${contract.contract_number}`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/contracts/${contract.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchContracts();
      else alert(data.error || tr('cancelError'));
    } catch (err) {
      console.error('[VSM] Cancel error:', err);
      alert(tr('cancelError'));
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB');
  };

  // Styles
  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const tableHeaderStyle = {
    padding: '10px 12px', fontSize: '12px', color: '#94a3b8',
    fontWeight: '600', textAlign: isRTL ? 'right' : 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  };
  const tableCellStyle = {
    padding: '12px', fontSize: '13px', color: '#e2e8f0',
    borderBottom: '1px solid rgba(255,255,255,0.04)'
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1300px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
              {tr('title')}
            </h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
              {pagination.total} {tr('subtitle')}
            </p>
          </div>
          <button
            onClick={() => navigate('/vsm/contracts/new')}
            style={{
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', padding: '9px 18px', borderRadius: '8px',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer'
            }}
          >
            {tr('add')}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder={tr('search')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ ...inputStyle, maxWidth: '300px' }}
          />
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ ...inputStyle, maxWidth: '180px', cursor: 'pointer' }}
          >
            <option value="">{tr('statusAll')}</option>
            <option value="active">{tr('statusActive')}</option>
            <option value="paused">{tr('statusPaused')}</option>
            <option value="expired">{tr('statusExpired')}</option>
            <option value="cancelled">{tr('statusCancel')}</option>
          </select>
        </div>

        {/* Table */}
        <div style={{
          background: 'rgba(30,41,59,0.5)', borderRadius: '12px',
          overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('loading')}</div>
          ) : contracts.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('noContracts')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'rgba(15,23,42,0.5)' }}>
                <tr>
                  <th style={tableHeaderStyle}>{tr('contractNo')}</th>
                  <th style={tableHeaderStyle}>{tr('contractTitle')}</th>
                  <th style={tableHeaderStyle}>{tr('period')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('clients')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('services')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('items')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('status')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => {
                  const statusInfo = STATUS_COLORS[c.status] || STATUS_COLORS.active;
                  return (
                    <tr key={c.id}
                        style={{ transition: 'background 0.15s', cursor: 'pointer' }}
                        onClick={() => navigate(`/vsm/contracts/${c.id}`)}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={tableCellStyle}>
                        <div style={{ fontFamily: 'monospace', fontWeight: '700', color: '#a5b4fc' }} dir="ltr">
                          {c.contract_number}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: '600', color: '#f1f5f9' }}>
                          {c.title || <span style={{ color: '#64748b', fontStyle: 'italic' }}>{tr('untitled')}</span>}
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ fontSize: '12px' }}>
                          {formatDate(c.start_date)}
                          {c.end_date && <> → {formatDate(c.end_date)}</>}
                        </div>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <span style={{
                          background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
                          padding: '4px 10px', borderRadius: '12px',
                          fontSize: '12px', fontWeight: '700'
                        }}>
                          {c.clients_count || 0}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <span style={{
                          background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
                          padding: '4px 10px', borderRadius: '12px',
                          fontSize: '12px', fontWeight: '700'
                        }}>
                          {c.services_count || 0}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <span style={{
                          background: 'rgba(255,111,0,0.15)', color: '#fb923c',
                          padding: '4px 10px', borderRadius: '12px',
                          fontSize: '12px', fontWeight: '700'
                        }}>
                          {c.items_count || 0}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <span style={{
                          background: statusInfo.bg, color: statusInfo.color,
                          padding: '4px 10px', borderRadius: '12px',
                          fontSize: '11px', fontWeight: '700'
                        }}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/vsm/contracts/${c.id}`); }}
                            style={{
                              background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                              border: '1px solid rgba(99,102,241,0.3)',
                              padding: '5px 10px', borderRadius: '6px',
                              fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                            }}
                          >
                            👁️
                          </button>
                          {c.status === 'active' && (
                            <button
                              onClick={(e) => handleCancel(c, e)}
                              style={{
                                background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                                border: '1px solid rgba(239,68,68,0.25)',
                                padding: '5px 10px', borderRadius: '6px',
                                fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                              }}
                            >
                              🚫
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
            <button
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              style={{
                background: pagination.page <= 1 ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page <= 1 ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >{tr('prev')}</button>
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>
              {tr('page')} {pagination.page} {tr('of')} {pagination.total_pages}
            </span>
            <button
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              style={{
                background: pagination.page >= pagination.total_pages ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page >= pagination.total_pages ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page >= pagination.total_pages ? 'not-allowed' : 'pointer'
              }}
            >{tr('next')}</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VSMContracts;
